/**
 * Support Zustand Store
 *
 * Manages all state for the Support & Communications section including:
 * - Support requests list with filters and pagination
 * - Active request details and messages
 * - AI flagged content list
 * - Queue summary statistics
 */
import { create } from 'zustand';
import { getApiClient, API_ENDPOINTS } from '@respectly/api-client';
import type {
  SupportRequest,
  SupportMessage,
  SupportListFilters,
  SupportQueueSummary,
  SupportRequestStatus,
  PaginationMeta,
  AIFlaggedContent,
  AddSupportMessageDto,
  AssignSupportRequestDto,
  ResolveSupportRequestDto,
  ReviewAiFlagDto,
  UpdateSupportRequestDto,
} from '@respectly/shared-types';

// ============================================
// Types
// ============================================

/** Error state */
interface SupportError {
  code: string;
  message: string;
}

// ============================================
// State Interface
// ============================================

export interface SupportState {
  // Support Requests List
  requests: SupportRequest[];
  pagination: PaginationMeta | null;
  isLoadingList: boolean;
  listError: SupportError | null;

  // Filters
  filters: SupportListFilters;

  // Active Request
  activeRequestId: string | null;
  activeRequest: SupportRequest | null;
  isLoadingRequest: boolean;
  requestError: SupportError | null;

  // Messages
  messages: SupportMessage[];
  isLoadingMessages: boolean;

  // Queue Summary
  queueSummary: SupportQueueSummary | null;
  isLoadingQueueSummary: boolean;

  // AI Flagged Content
  aiFlags: AIFlaggedContent[];
  isLoadingAiFlags: boolean;
  aiPagination: PaginationMeta | null;

  // Action States
  isPerformingAction: boolean;
  actionError: SupportError | null;

  // Actions - List
  loadRequests: (page?: number) => Promise<void>;
  refreshRequests: () => Promise<void>;

  // Actions - Filters
  setFilter: <K extends keyof SupportListFilters>(
    key: K,
    value: SupportListFilters[K]
  ) => void;
  clearFilters: () => void;

  // Actions - Active Request
  loadRequest: (id: string) => Promise<void>;
  selectRequest: (id: string) => void;
  clearActiveRequest: () => void;

  // Actions - Messages
  loadMessages: (requestId: string) => Promise<void>;
  addMessage: (requestId: string, dto: AddSupportMessageDto) => Promise<boolean>;

  // Actions - Request Lifecycle
  assignRequest: (id: string, dto: AssignSupportRequestDto) => Promise<boolean>;
  resolveRequest: (id: string, dto?: ResolveSupportRequestDto) => Promise<boolean>;
  reopenRequest: (id: string) => Promise<boolean>;
  closeRequest: (id: string) => Promise<boolean>;
  updateRequest: (id: string, dto: UpdateSupportRequestDto) => Promise<boolean>;

  // Actions - Queue
  loadQueueSummary: () => Promise<void>;

  // Actions - AI Flags
  loadAiFlags: (page?: number) => Promise<void>;
  reviewAiFlag: (id: string, dto: ReviewAiFlagDto) => Promise<boolean>;

  // Utility
  clearError: () => void;
  reset: () => void;
}

// ============================================
// Initial State
// ============================================

const getInitialState = () => ({
  // Support Requests List
  requests: [] as SupportRequest[],
  pagination: null as PaginationMeta | null,
  isLoadingList: false,
  listError: null as SupportError | null,

  // Filters
  filters: {} as SupportListFilters,

  // Active Request
  activeRequestId: null as string | null,
  activeRequest: null as SupportRequest | null,
  isLoadingRequest: false,
  requestError: null as SupportError | null,

  // Messages
  messages: [] as SupportMessage[],
  isLoadingMessages: false,

  // Queue Summary
  queueSummary: null as SupportQueueSummary | null,
  isLoadingQueueSummary: false,

  // AI Flagged Content
  aiFlags: [] as AIFlaggedContent[],
  isLoadingAiFlags: false,
  aiPagination: null as PaginationMeta | null,

  // Action States
  isPerformingAction: false,
  actionError: null as SupportError | null,
});

// ============================================
// Store Implementation
// ============================================

export const useSupportStore = create<SupportState>((set, get) => ({
  ...getInitialState(),

  // ============================================
  // List Actions
  // ============================================

  loadRequests: async (page = 1) => {
    set({ isLoadingList: true, listError: null });
    try {
      const api = getApiClient();
      const { filters } = get();
      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('limit', '20');

      // Apply filters
      if (filters.search) params.set('search', filters.search);
      if (filters.status) params.set('status', filters.status);
      if (filters.priority) params.set('priority', filters.priority);
      if (filters.category) params.set('category', filters.category);
      if (filters.assignedToId) params.set('assignedToId', filters.assignedToId);
      if (filters.tenantId) params.set('tenantId', filters.tenantId);
      if (filters.dateRange && filters.dateRange !== 'all')
        params.set('dateRange', filters.dateRange);

      const response = await api.get<{ data: SupportRequest[]; pagination: PaginationMeta }>(
        `${API_ENDPOINTS.SUPER_ADMIN.SUPPORT.REQUESTS}?${params.toString()}`
      );

      set({
        requests: response.data,
        pagination: response.pagination,
        isLoadingList: false,
      });
    } catch (error) {
      set({
        isLoadingList: false,
        listError: {
          code: 'LOAD_REQUESTS_FAILED',
          message: error instanceof Error ? error.message : 'Failed to load requests',
        },
      });
    }
  },

  refreshRequests: async () => {
    const { pagination } = get();
    await get().loadRequests(pagination?.page || 1);
  },

  // ============================================
  // Filter Actions
  // ============================================

  setFilter: (key, value) => {
    set((state) => ({
      filters: { ...state.filters, [key]: value },
    }));
    get().loadRequests(1);
  },

  clearFilters: () => {
    set({ filters: {} });
    get().loadRequests(1);
  },

  // ============================================
  // Active Request Actions
  // ============================================

  loadRequest: async (id: string) => {
    set({ activeRequestId: id, isLoadingRequest: true, requestError: null });
    try {
      const api = getApiClient();
      const response = await api.get<SupportRequest>(API_ENDPOINTS.SUPER_ADMIN.SUPPORT.REQUEST(id));

      set({ activeRequest: response, isLoadingRequest: false });

      // Also load messages
      get().loadMessages(id);
    } catch (error) {
      set({
        isLoadingRequest: false,
        requestError: {
          code: 'LOAD_REQUEST_FAILED',
          message: error instanceof Error ? error.message : 'Failed to load request',
        },
      });
    }
  },

  selectRequest: (id: string) => {
    const { requests } = get();
    const request = requests.find((r) => r.id === id);
    if (request) {
      set({ activeRequestId: id, activeRequest: request });
      get().loadMessages(id);
    } else {
      get().loadRequest(id);
    }
  },

  clearActiveRequest: () => {
    set({
      activeRequestId: null,
      activeRequest: null,
      messages: [],
      requestError: null,
    });
  },

  // ============================================
  // Message Actions
  // ============================================

  loadMessages: async (requestId: string) => {
    set({ isLoadingMessages: true });
    try {
      const api = getApiClient();
      const response = await api.get<SupportMessage[]>(API_ENDPOINTS.SUPER_ADMIN.SUPPORT.MESSAGES(requestId));

      set({ messages: response, isLoadingMessages: false });
    } catch {
      set({ isLoadingMessages: false });
    }
  },

  addMessage: async (requestId: string, dto: AddSupportMessageDto): Promise<boolean> => {
    set({ isPerformingAction: true, actionError: null });
    try {
      const api = getApiClient();
      const response = await api.post<SupportMessage>(API_ENDPOINTS.SUPER_ADMIN.SUPPORT.MESSAGES(requestId), dto);

      set((state) => ({
        messages: [...state.messages, response],
        isPerformingAction: false,
      }));
      return true;
    } catch (error) {
      set({
        isPerformingAction: false,
        actionError: {
          code: 'ADD_MESSAGE_FAILED',
          message: error instanceof Error ? error.message : 'Failed to add message',
        },
      });
      return false;
    }
  },

  // ============================================
  // Request Lifecycle Actions
  // ============================================

  assignRequest: async (id: string, dto: AssignSupportRequestDto): Promise<boolean> => {
    set({ isPerformingAction: true, actionError: null });
    try {
      const api = getApiClient();
      await api.post(API_ENDPOINTS.SUPER_ADMIN.SUPPORT.ASSIGN(id), dto);

      // Update local state
      set((state) => ({
        requests: state.requests.map((r) =>
          r.id === id ? { ...r, assignedToId: dto.assignedToId } : r
        ),
        activeRequest:
          state.activeRequest?.id === id
            ? { ...state.activeRequest, assignedToId: dto.assignedToId }
            : state.activeRequest,
        isPerformingAction: false,
      }));

      await get().loadQueueSummary();
      return true;
    } catch (error) {
      set({
        isPerformingAction: false,
        actionError: {
          code: 'ASSIGN_FAILED',
          message: error instanceof Error ? error.message : 'Failed to assign request',
        },
      });
      return false;
    }
  },

  resolveRequest: async (id: string, dto?: ResolveSupportRequestDto): Promise<boolean> => {
    set({ isPerformingAction: true, actionError: null });
    try {
      const api = getApiClient();
      await api.post(API_ENDPOINTS.SUPER_ADMIN.SUPPORT.RESOLVE(id), dto || {});

      // Update local state
      set((state) => ({
        requests: state.requests.map((r) =>
          r.id === id
            ? { ...r, status: 'resolved' as SupportRequestStatus, resolvedAt: new Date() }
            : r
        ),
        activeRequest:
          state.activeRequest?.id === id
            ? {
                ...state.activeRequest,
                status: 'resolved' as SupportRequestStatus,
                resolvedAt: new Date(),
              }
            : state.activeRequest,
        isPerformingAction: false,
      }));

      await get().loadQueueSummary();
      return true;
    } catch (error) {
      set({
        isPerformingAction: false,
        actionError: {
          code: 'RESOLVE_FAILED',
          message: error instanceof Error ? error.message : 'Failed to resolve request',
        },
      });
      return false;
    }
  },

  reopenRequest: async (id: string): Promise<boolean> => {
    set({ isPerformingAction: true, actionError: null });
    try {
      const api = getApiClient();
      await api.post(API_ENDPOINTS.SUPER_ADMIN.SUPPORT.REOPEN(id), {});

      set((state) => ({
        requests: state.requests.map((r) =>
          r.id === id
            ? { ...r, status: 'open' as SupportRequestStatus, resolvedAt: null }
            : r
        ),
        activeRequest:
          state.activeRequest?.id === id
            ? {
                ...state.activeRequest,
                status: 'open' as SupportRequestStatus,
                resolvedAt: null,
              }
            : state.activeRequest,
        isPerformingAction: false,
      }));

      await get().loadQueueSummary();
      return true;
    } catch (error) {
      set({
        isPerformingAction: false,
        actionError: {
          code: 'REOPEN_FAILED',
          message: error instanceof Error ? error.message : 'Failed to reopen request',
        },
      });
      return false;
    }
  },

  closeRequest: async (id: string): Promise<boolean> => {
    set({ isPerformingAction: true, actionError: null });
    try {
      const api = getApiClient();
      await api.post(API_ENDPOINTS.SUPER_ADMIN.SUPPORT.CLOSE(id), {});

      set((state) => ({
        requests: state.requests.map((r) =>
          r.id === id ? { ...r, status: 'closed' as SupportRequestStatus } : r
        ),
        activeRequest:
          state.activeRequest?.id === id
            ? { ...state.activeRequest, status: 'closed' as SupportRequestStatus }
            : state.activeRequest,
        isPerformingAction: false,
      }));

      await get().loadQueueSummary();
      return true;
    } catch (error) {
      set({
        isPerformingAction: false,
        actionError: {
          code: 'CLOSE_FAILED',
          message: error instanceof Error ? error.message : 'Failed to close request',
        },
      });
      return false;
    }
  },

  updateRequest: async (id: string, dto: UpdateSupportRequestDto): Promise<boolean> => {
    set({ isPerformingAction: true, actionError: null });
    try {
      const api = getApiClient();
      await api.put(API_ENDPOINTS.SUPER_ADMIN.SUPPORT.UPDATE_REQUEST(id), dto);

      set((state) => ({
        requests: state.requests.map((r) =>
          r.id === id ? { ...r, ...dto } : r
        ),
        activeRequest:
          state.activeRequest?.id === id
            ? { ...state.activeRequest, ...dto }
            : state.activeRequest,
        isPerformingAction: false,
      }));

      return true;
    } catch (error) {
      set({
        isPerformingAction: false,
        actionError: {
          code: 'UPDATE_FAILED',
          message: error instanceof Error ? error.message : 'Failed to update request',
        },
      });
      return false;
    }
  },

  // ============================================
  // Queue Summary Actions
  // ============================================

  loadQueueSummary: async () => {
    set({ isLoadingQueueSummary: true });
    try {
      const api = getApiClient();
      const response = await api.get<SupportQueueSummary>(API_ENDPOINTS.SUPER_ADMIN.SUPPORT.QUEUE_SUMMARY);

      set({ queueSummary: response, isLoadingQueueSummary: false });
    } catch {
      set({ isLoadingQueueSummary: false });
    }
  },

  // ============================================
  // AI Flags Actions
  // ============================================

  loadAiFlags: async (page = 1) => {
    set({ isLoadingAiFlags: true });
    try {
      const api = getApiClient();
      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('limit', '20');
      params.set('status', 'pending'); // Default to pending

      const response = await api.get<{ data: AIFlaggedContent[]; pagination: PaginationMeta }>(
        `${API_ENDPOINTS.SUPER_ADMIN.SUPPORT.AI_FLAGS}?${params.toString()}`
      );

      set({
        aiFlags: response.data,
        aiPagination: response.pagination,
        isLoadingAiFlags: false,
      });
    } catch {
      set({ isLoadingAiFlags: false });
    }
  },

  reviewAiFlag: async (id: string, dto: ReviewAiFlagDto): Promise<boolean> => {
    set({ isPerformingAction: true, actionError: null });
    try {
      const api = getApiClient();
      await api.put(API_ENDPOINTS.SUPER_ADMIN.SUPPORT.AI_FLAG_REVIEW(id), dto);

      // Remove from list
      set((state) => ({
        aiFlags: state.aiFlags.filter((f) => f.id !== id),
        isPerformingAction: false,
      }));

      await get().loadQueueSummary();
      return true;
    } catch (error) {
      set({
        isPerformingAction: false,
        actionError: {
          code: 'REVIEW_FLAG_FAILED',
          message: error instanceof Error ? error.message : 'Failed to review flag',
        },
      });
      return false;
    }
  },

  // ============================================
  // Utility Actions
  // ============================================

  clearError: () => {
    set({ listError: null, requestError: null, actionError: null });
  },

  reset: () => {
    set(getInitialState());
  },
}));

// ============================================
// Selector Hooks
// ============================================

/** Get request count by status */
export const useRequestCountByStatus = (status: SupportRequestStatus) => {
  return useSupportStore((state) =>
    state.requests.filter((r) => r.status === status).length
  );
};

/** Get urgent requests */
export const useUrgentRequests = () => {
  return useSupportStore((state) =>
    state.requests.filter((r) => r.priority === 'urgent' && r.status !== 'closed')
  );
};

/** Check if any loading state is active */
export const useIsSupportLoading = () => {
  const isLoadingList = useSupportStore((state) => state.isLoadingList);
  const isLoadingRequest = useSupportStore((state) => state.isLoadingRequest);
  const isPerformingAction = useSupportStore((state) => state.isPerformingAction);
  return isLoadingList || isLoadingRequest || isPerformingAction;
};

/** Get active filters count */
export const useActiveFiltersCount = () => {
  return useSupportStore((state) => {
    let count = 0;
    if (state.filters.search) count++;
    if (state.filters.status) count++;
    if (state.filters.priority) count++;
    if (state.filters.category) count++;
    if (state.filters.assignedToId) count++;
    if (state.filters.tenantId) count++;
    if (state.filters.dateRange && state.filters.dateRange !== 'all') count++;
    return count;
  });
};
